// ** Modules
import React, { type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import FadeInView from '@/components/common/FadeInView';
import { Icon } from '@/components/common/icons';

export type ComponentBlocksDropdownAccordionItem = {
	id: string;
	title: string;
	copy?: string | ReactNode;
	image?: {
		src: string;
		alt: string;
	};
};

// Image dimensions for accordion items — sub-pixel values intentional.
const ACCORDION_IMAGE_WIDTH = '374.45px';
const ACCORDION_IMAGE_HEIGHT = '250.167px';
const ACCORDION_IMAGE_ASPECT_RATIO = '232 / 155';

export type ComponentBlocksDropdownAccordionBlockProps = {
	items: readonly ComponentBlocksDropdownAccordionItem[];
	allowMultipleExpanded?: boolean;
	defaultExpandedIds?: string[];
	maxWidthPx?: number;
	autoAdvanceDuration?: number;
	/** When true, hides the auto-advance progress bar and countdown behaviour (e.g. event detail mobile). */
	disableAutoAdvance?: boolean;
	/** Use primary (orange) accordion titles to match editorial headings. */
	titleColor?: 'default' | 'primary';
};

const ComponentBlocksDropdownAccordionBlock = ({
	items,
	allowMultipleExpanded = true,
	defaultExpandedIds,
	maxWidthPx,
	autoAdvanceDuration = 8,
	disableAutoAdvance = false,
	titleColor = 'default'
}: ComponentBlocksDropdownAccordionBlockProps) => {
	const theme = useTheme();

	const [expandedIds, setExpandedIds] = React.useState<string[]>(
		defaultExpandedIds ?? (items.length > 0 ? [items[0].id] : [])
	);

	const [activeIndex, setActiveIndex] = React.useState(0);
	const activeId = items[activeIndex]?.id;

	const [paused, setPaused] = React.useState(false);

	const toggle = (id: string) => {
		setPaused(true);

		const clickedIndex = items.findIndex((item) => item.id === id);
		if (clickedIndex !== -1) setActiveIndex(clickedIndex);

		setExpandedIds((prev) => {
			const isExpanded = prev.includes(id);
			if (allowMultipleExpanded) {
				return isExpanded ? prev.filter((x) => x !== id) : [...prev, id];
			}
			return isExpanded ? [] : [id];
		});
	};

	const handleCountdownComplete = React.useCallback(() => {
		if (items.length < 2) return;
		const nextIndex = (activeIndex + 1) % items.length;
		const nextId = items[nextIndex].id;
		setActiveIndex(nextIndex);
		setExpandedIds([nextId]);
	}, [activeIndex, items]);

	return (
		<FadeInView>
			<Box
				sx={{
					width: '100%',
					...(maxWidthPx ? { maxWidth: `${maxWidthPx}px`, mx: 'auto' } : null)
				}}
			>
				{items.map((item, index) => {
					const isExpanded = expandedIds.includes(item.id);
					const isActive = item.id === activeId;

					return (
						<FadeInView key={item.id} delay={index * 0.08} offsetY={12}>
							<Box sx={{ width: '100%' }}>
								<ButtonBase
									onClick={() => toggle(item.id)}
									aria-expanded={isExpanded}
									aria-controls={`dropdown-accordion-panel-${item.id}`}
									disableRipple
									sx={{
										width: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										pl: 0,
										pr: 3,
										pt: 6,
										pb: 4,
										textAlign: 'left',
										'&:hover, &:active, &:focusVisible': {
											backgroundColor: 'transparent',
											outline: 'none'
										},
										'&:hover .chevron-wrap': {
											opacity: 0.5,
											transform: 'scale(1.25)'
										}
									}}
								>
									<Typography
										variant="title-sm"
										sx={{
											color: titleColor === 'primary' ? theme.palette.primary.main : theme.palette.text.primary
										}}
									>
										{item.title}
									</Typography>

									<Box
										className="chevron-wrap"
										sx={{
											display: 'flex',
											alignItems: 'center',
											transition: 'opacity 220ms ease, transform 220ms ease'
										}}
									>
										<Icon
											name="chevron"
											expanded={isExpanded}
											width={16}
											height={8}
											color={theme.palette.text.secondary ?? theme.palette.text.primary}
										/>
									</Box>
								</ButtonBase>

								<AnimatePresence initial={false}>
									{isExpanded && (
										<motion.div
											id={`dropdown-accordion-panel-${item.id}`}
											key={`panel-${item.id}`}
											initial={{ height: 0, opacity: 0 }}
											animate={{ height: 'auto', opacity: 1 }}
											exit={{ height: 0, opacity: 0 }}
											transition={{
												height: { duration: 0.55, ease: [0.4, 0, 0.2, 1] },
												opacity: { duration: 0.4, ease: 'easeInOut' }
											}}
											style={{ overflow: 'hidden' }}
										>
											<Stack
												spacing={3}
												sx={{
													pt: 3,
													pb: 6,
													pl: 4,
													pr: 4
												}}
											>
												{item.copy != null &&
													item.copy !== '' &&
													(typeof item.copy === 'string' ? (
														<Typography
															variant="body-md"
															sx={{
																width: '100%',
																maxWidth: { xs: '800px', md: '1040px' },
																color: theme.palette.text.secondary ?? theme.palette.text.primary
															}}
														>
															{item.copy}
														</Typography>
													) : (
														<Box
															sx={{
																width: '100%',
																maxWidth: { xs: '800px', md: '1040px' },
																typography: 'body-md',
																color: theme.palette.text.secondary ?? theme.palette.text.primary
															}}
														>
															{item.copy}
														</Box>
													))}

												{item.image && (
													<Box
														component="img"
														src={item.image.src}
														alt={item.image.alt}
														sx={{
															width: { xs: '100%', sm: ACCORDION_IMAGE_WIDTH },
															maxWidth: ACCORDION_IMAGE_WIDTH,
															height: ACCORDION_IMAGE_HEIGHT,
															aspectRatio: ACCORDION_IMAGE_ASPECT_RATIO,
															objectFit: 'cover',
															display: 'block',
															borderRadius: 2,
															mt: item.copy != null && item.copy !== '' ? 0 : 3
														}}
													/>
												)}
											</Stack>
										</motion.div>
									)}
								</AnimatePresence>

								{!disableAutoAdvance && isActive && !paused ? (
									<motion.div
										key={activeId}
										initial={{ scaleX: 1 }}
										animate={{ scaleX: 0 }}
										transition={{
											duration: autoAdvanceDuration,
											ease: 'linear'
										}}
										onAnimationComplete={handleCountdownComplete}
										style={{
											width: '100%',
											height: 1,
											backgroundColor: 'rgba(0, 0, 0, 0.18)',
											transformOrigin: 'right'
										}}
									/>
								) : (
									<Box
										sx={{
											width: '100%',
											borderBottom: '1px solid rgba(0, 0, 0, 0.18)'
										}}
									/>
								)}
							</Box>
						</FadeInView>
					);
				})}
			</Box>
		</FadeInView>
	);
};

export default ComponentBlocksDropdownAccordionBlock;
